const mapLineItemFromSalesOrderItems = (items) => {
    if (items == null) {
        return null;
    }

    return items.map(item => _mapLineItemFromSalesOrderItem(item));
};

const mapLineItemsFromSalesOrderDetailLineItemDetails = (salesOrderDetail) => {
    if (salesOrderDetail == null) {
        return null;
    }

    const lineItemDetails = salesOrderDetail.LineItemDetails;
    return lineItemDetails && lineItemDetails.map(lineItemDetail =>
        _mapLineItemFromSalesOrderDetailLineItemDetail(lineItemDetail));
};

const mapLineItemsToSalesOrderRequestItem = (order) => {
    if (!order || order.lineItems == null) {
        return null;
    }

    return order.lineItems.map(lineItem => _mapLineItemToSalesOrderRequestItem(lineItem));
};

const mapLineItemsToSalesOrderDetailLineItems = (lineItems) => {
    if (lineItems == null) {
        return [];
    }

    return lineItems.map(lineItem => _mapLineItemToSalesOrderDetailLineItemDetail(lineItem));
};

const mapShippingFromSalesOrderDetailShipping = (shipping) => {
    if (shipping == null) {
        return null;
    }

    return {
        shippingParties: _mapSalesOrderDetailShippingParties(shipping.ShippingParty),
        shippingMethod: _mapShippingMethodFromSalesOrderDetail(shipping.ShippingMethod),
        tracking: shipping.Tracking
    }
};

const mapShippingToSalesOrderDetailShipping = (order) => {
    const shipping = order && order.shipping || {};
    const shippingParties = shipping && shipping.shippingParties || [];
    const shippingMethod = shipping && shipping.shippingMethod || null;

    return {
        ShippingParty: _mapShippingPartiesToSalesOrderDetailShippingParty(shippingParties),
        ShippingMethod: shippingMethod &&
            _mapShippingMethodToSalesOrderDetailShippingMethod(shipping.shippingMethod),
        ...(shipping != null && shipping.tracking != null &&
            { Tracking: shipping.tracking } ||
            ((shipping != null && typeof(shipping.tracking) != "undefined") ? { Tracking: null } : {}))
    }
};

const mapAdrFromSalesOrderDetail = (salesOrderDetail) => {
    const adrContractLength = getValueFromSalesOrderCustom(salesOrderDetail.Custom, 'ADR_CONTRACT_LENGTH');
    return {
        adrOverrideId: salesOrderDetail.ADROverrideID,
        ...(adrContractLength && {adrContractLength})
    }
}

const mapOrderTotalsFromSalesOrderDetail = (orderTotals) => {
    if (!orderTotals) {
        return null;
    }

    const installmentFee = getValueFromSalesOrderCustom(orderTotals.Custom, 'FEE'),
        installmentTem = getValueFromSalesOrderCustom(orderTotals.Custom, 'TEM'),
        installmentTea = getValueFromSalesOrderCustom(orderTotals.Custom, 'TEA'),
        installmentCft = getValueFromSalesOrderCustom(orderTotals.Custom, 'CFT'),
        suppTaxOne = getValueFromSalesOrderCustom(orderTotals.Custom, 'SUPP_TAX_ONE');

    return {
        pointsUsed: orderTotals.PointsUsed,
        psv: orderTotals.PSV,
        cv: orderTotals.CV,
        sharingBonus: orderTotals.SharingBonus,
        savings: orderTotals.Savings,
        shipping: orderTotals.Shipping,
        taxBase: orderTotals.TaxBase,
        itemSubtotal: orderTotals.ItemSubtotal,
        grandTotal: orderTotals.GrandTotal,
        tax: orderTotals.Tax,
        rounding: orderTotals.Rounding,
        taxInclusive: orderTotals.TaxInclusive,

        // Custom Fields
        ...(installmentFee && {installmentFee}),
        ...(installmentTem && {installmentTem}),
        ...(installmentTea && {installmentTea}),
        ...(installmentCft && {installmentCft}),
        ...(suppTaxOne && {suppTaxOne})
    };
}

const mapToOrderTotals = (order) => {
    if (!order || !order.orderTotals) {
        return null;
    }

    return {
        PointsUsed: order.orderTotals.pointsUsed,
        PSV: order.orderTotals.psv,
        CV: order.orderTotals.cv,
        SharingBonus: order.orderTotals.sharingBonus,
        Savings: order.orderTotals.savings,
        Shipping: order.orderTotals.shipping,
        TaxBase: order.orderTotals.taxBase,
        ItemSubtotal: order.orderTotals.itemSubtotal,
        GrandTotal: order.orderTotals.grandTotal,
        Tax: order.orderTotals.tax,
        TaxInclusive: order.orderTotals.taxInclusive,
        Rounding: order.orderTotals.rounding,
        Custom: [
            ...(order.orderTotals.installmentFee && [{Key: 'FEE', Value: order.orderTotals.installmentFee}] || []),
            ...(order.orderTotals.installmentTem && [{Key: 'TEM', Value: order.orderTotals.installmentTem}] || []),
            ...(order.orderTotals.installmentTea && [{Key: 'TEA', Value: order.orderTotals.installmentTea}] || []),
            ...(order.orderTotals.installmentCft && [{Key: 'CFT', Value: order.orderTotals.installmentCft}] || []),
            ...(order.orderTotals.suppTaxOne && [{Key: 'SUPP_TAX_ONE', Value: order.orderTotals.suppTaxOne}] || [])
        ]
    }
}

const mapPaymentsFromSalesOrder = (payments) => {
    if (payments == null) {
        return [];
    }

    return payments.map(payment => _mapPaymentFromSalesOrder(payment));
};

const mapToSalesOrderPayments = (order) => {
    if (!order || order.payments == null) {
        return [];
    }

    return order.payments.map(payment => _mapToSalesOrderPayment(payment));
};

const mapMessagesFromSalesOrder = (messages) => {
    if (messages == null) {
        return null;
    }

    return messages.map(message => _mapMessageFromSalesOrder(message));
};

const mapMessagesToSalesOrder = (messages) => {
    if (messages == null) {
        return [];
    }

    return messages.map(m => _mapMessageToSalesOrder(m));
};

/**
 * Read the value from a custom field with specified type and key
 * @param {Object[]} customList  array of custom fields on the sales order object
 * @param {string} key
 * @param {string=} type
 * @returns {null|*}
 */
const getValueFromSalesOrderCustom = (customList, key, type) => {
    if (!Array.isArray(customList) || !key) {
        return null;
    }
    const cust = customList.find(custom => {
        return type && type.length ?
            custom.Type === type && custom.Key === key :
            custom.Key === key
    });
    return cust ? cust.Value : null;
}

/*** PRIVATE FUNCTIONS ***/
const _mapLineItemToSalesOrderRequestItem = (lineItem) => {
    if (lineItem == null) {
        return null;
    }

    return {
        LineID: lineItem.lineId,
        ParentID: lineItem.parentId,
        SKU: lineItem.sku,
        RequestedQuantity: lineItem.quantity,
        OneTime: lineItem.oneTime,
        Action: lineItem.action || 'ADD',
        Custom: [
            ...(lineItem.agelocmeCode && [{Key: 'ID', Type: 'CUST_MAT', Value: lineItem.agelocmeCode}] || []),
            ...(lineItem.agelocmeName && [{Key: 'NAME', Type: 'CUST_MAT', Value: lineItem.agelocmeName}] || []),
            ...(lineItem.agelocmeLabel && [{Key: 'LABEL', Type: 'CUST_MAT', Value: lineItem.agelocmeLabel}] || []),
            ...(lineItem.isExclusive && [{Key: 'IS_EXCLUSIVE', Type: 'QUALIFICATION', Value: lineItem.isExclusive}] || []),
            ...(lineItem.gpid && [{Key: 'GPID', Type: 'GPID', Value: lineItem.gpid}] || [])
        ]
    };
};

const _mapLineItemToSalesOrderDetailLineItemDetail = (lineItem) => {
    if (lineItem == null) {
        return {};
    }

    return {
        LineItem: {
            LineID: lineItem.lineId,
            ParentID: lineItem.parentId,
            SKU: lineItem.sku,
            RequestedQuantity: lineItem.quantity,
            PayWithPoints: lineItem.payWithPoints,
            OneTime: lineItem.oneTime,
            Action: 'ADD',
            Pricing: lineItem.pricing,
            Custom: [
                ...(lineItem.agelocmeCode && [{Key: 'ID', Type: 'CUST_MAT', Value: lineItem.agelocmeCode}] || []),
                ...(lineItem.agelocmeName && [{Key: 'NAME', Type: 'CUST_MAT', Value: lineItem.agelocmeName}] || []),
                ...(lineItem.agelocmeLabel && [{Key: 'LABEL', Type: 'CUST_MAT', Value: lineItem.agelocmeLabel}] || []),
                ...(lineItem.isExclusive && [{Key: 'IS_EXCLUSIVE', Type: 'QUALIFICATION', Value: lineItem.isExclusive}] || []),
                ...(lineItem.gpid && [{Key: 'GPID', Type: 'GPID', Value: lineItem.gpid}] || [])
            ]
        },
        Name: lineItem.title,
        ReplacedSKU: lineItem.replacedSku,
        AvailableQuantity: lineItem.availableQuantity,
        TotalAvailableQuantity: lineItem.totalAvailableQuantity,
        Price: lineItem.price,
        Points: lineItem.points,
        PointsUsed: lineItem.pointsUsed,
        FreightPromoSavings: lineItem.freightPromoSavings,
        ProductPromoSavings: lineItem.productPromoSavings,
        Tax: lineItem.tax,
        TaxBase: lineItem.taxBase,
        PSV: lineItem.psv,
        CV: lineItem.cv,
        Freight: lineItem.freight,
        BackorderAvailableDate: lineItem.backOrderAvailableDate,
        BackOrdered: lineItem.backOrdered,
        InvalidSku: lineItem.invalidSku,
        GrandFatheredPricing: lineItem.grandFatheredPricing,
        AddedBySystem: lineItem.addedBySystem,
        Plant: lineItem.plant,
        StorageLocation: lineItem.storageLocation,
        SharingBonus: lineItem.sharingBonus,
        Rounding: lineItem.rounding,
        TaxInclusive: lineItem.taxInclusive
    }
};

const _mapShippingPartiesToSalesOrderDetailShippingParty = (shippingParties) => {
    if (shippingParties == null) {
        return [];
    }
    return shippingParties.map(
        sp => _mapShippingPartyToSalesOrderDetailShippingParty(sp)) || [];
};

const _mapShippingPartyToSalesOrderDetailShippingParty = (shippingParty) => {
    if (shippingParty == null) {
        return {};
    }

    return {
        ShippingMemberID: shippingParty.shippingMemberId,
        Type: shippingParty.type,
        ShippingAddress: shippingParty.shippingAddress &&
            _mapShippingAddressToSalesOrderDetailShippingAddress(shippingParty.shippingAddress),
        Custom: [
            ...(shippingParty.addressType && [{Type: 'ADDRESS', Key: 'TYPE', Value: shippingParty.addressType}] || []),
            ...(shippingParty.pickupId && [{Type: 'PICKUPID', Key: 'TYPE', Value: shippingParty.pickupId}] || []),
            ...(shippingParty.pickupLocker && [{Type: 'PICKUPSR', Key: 'TYPE', Value: shippingParty.pickupLocker}] || []),
            ...(shippingParty.carrierCode && [{Type: 'CARRIER_CODE', Key: 'TYPE', Value: shippingParty.carrierCode}] || [])
        ]
    };
};

const _mapShippingAddressToSalesOrderDetailShippingAddress = (shippingAddress) => {
    if (shippingAddress == null) {
        return null;
    }

    return {
        ...(shippingAddress.id != null && { ID: shippingAddress.id } || {}),
        Name: shippingAddress.name,
        ...(shippingAddress.careOfName != null && { CareOfName: shippingAddress.careOfName } || {}),
        Address: _mapAddressToSalesOrderDetailAddress(shippingAddress.address),
        Contact: _mapAddressContactToSalesOrderContacts(shippingAddress),
        ...(shippingAddress.instructions != null && { Instructions: shippingAddress.instructions } || {})
    }
};

const _mapAddressToSalesOrderDetailAddress = (address) => {
    if (address == null) {
        return null;
    }
    // Todo add email address
    return {
        Street1: address.street1,
        Street2: address.street2,
        City: address.city,
        ...(address.district != null && { District: address.district } || {}),
        ...(address.districtDesc != null &&
            { DistrictDesc: address.districtDesc } ||
            (typeof(address.districtDesc) != "undefined" ? { DistrictDesc: null } : {})),
        Region: address.region,
        ...(address.regionDesc != null &&
            { RegionDesc: address.regionDesc } ||
            (typeof(address.regionDesc) != "undefined" ? { RegionDesc: null } : {})),
        PostalCode: address.postalCode,
        CountryCode: address.countryCode,
        ...(address.countryCodeDesc != null &&
            { CountryCodeDesc: address.countryCodeDesc } ||
            (typeof(address.countryCodeDesc) != "undefined" ? { CountryCodeDesc: null } : {})),
        ...(address.internationalAddresses != null &&
            { InternationalAddresses: address.internationalAddresses } ||
            (typeof(address.internationalAddresses) != "undefined" ? { InternationalAddresses: null } : {}))
    };
};

const _mapAddressContactToSalesOrderContacts = (shippingAddress) => {
    const contacts = [];
    if (shippingAddress == null) {
        return contacts;
    }
    if (shippingAddress.email) {
        contacts.push({
            Country: shippingAddress.address.countryCode,
            Value: {
                value: shippingAddress.email,
                Type: "EMAIL"
            }
        });
    }
    // Mobile phone is now primary.  Only map the homePhone if we don't have mobile.
    if (shippingAddress.homePhone && !shippingAddress.mobilePhone) {
        contacts.push({
            Country: shippingAddress.phoneCountry || shippingAddress.address.countryCode,
            Value: {
                value: shippingAddress.homePhone,
                Type: "HOMEPHONE"
            }
        });
    }
    if (shippingAddress.mobilePhone) {
        contacts.push({
            Country: shippingAddress.mobilePhoneCountry || shippingAddress.address.countryCode,
            Value: {
                value: shippingAddress.mobilePhone,
                Type: "MOBILEPHONE"
            }
        });
    }
    return contacts;
}

const _mapShippingMethodToSalesOrderDetailShippingMethod = (shippingMethod) => {
    if (shippingMethod == null) {
        return {};
    }

    return {
        ...(shippingMethod.code != null && {Code: shippingMethod.code} || {}),
        ...(shippingMethod.default != null && {Default: shippingMethod.default} || {}),
        ...(shippingMethod.description != null && {Description: shippingMethod.description} || {}),
        ...(shippingMethod.cost != null && {Cost: shippingMethod.cost} || {}),
        ...(shippingMethod.pickUpLocation != null && {PickUpLocation: shippingMethod.pickUpLocation} || {}),
        ...(typeof(shippingMethod.currency) != "undefined" && {Currency: shippingMethod.currency} || {})
    };
};

const _mapToSalesOrderCreditCard = (payment) => {
    if (payment.creditCard == null) {
        return null;
    }

    return {
        TransactionType: _mapTransactionTypeToSalesOrder(payment.installments),
        Installments: payment.installments,
        Type: payment.creditCard.type,
        CardNumber: payment.paymentNumber,
        AuthorizationCode: "",
        Name: payment.paymentName,
        ExpirationMonth: payment.creditCard.expirationMonth,
        ExpirationYear: payment.creditCard.expirationYear,
        VerificationCode: payment.creditCard.verificationCode,
        PostalCode: payment.billingAddress.postalCode,
        EncryptedCardNumber: payment.creditCard.encryptedCardNumber,
        TrackOne: payment.creditCard.trackOne
    };
};

const _mapTransactionTypeToSalesOrder = (installments) => {
    if (!installments) {
        return null;
    }

    return {
        value: 'INSTALLMENT',
        PaymentPeriod: installments
    };
};

const _mapLineItemFromSalesOrderItem = (item) => {
    if (item == null) {
        return null;
    }
    const agelocmeCode = getValueFromSalesOrderCustom(item.Custom, 'ID', 'CUST_MAT'),
        agelocmeName = getValueFromSalesOrderCustom(item.Custom, 'NAME', 'CUST_MAT'),
        agelocmeLabel = getValueFromSalesOrderCustom(item.Custom, 'LABEL', 'CUST_MAT'),
        isExclusive = getValueFromSalesOrderCustom(item.Custom, 'IS_EXCLUSIVE', 'QUALIFICATION'),
        gpid = getValueFromSalesOrderCustom(item.Custom, 'GPID', 'GPID');

    return {
        lineId: item.LineID,
        parentId: item.parentID,
        sku: item.SKU,
        quantity: item.RequestedQuantity,
        oneTime: item.OneTime,
        // Map custom
        ...(agelocmeCode && {agelocmeCode}),
        ...(agelocmeName && {agelocmeName}),
        ...(agelocmeLabel && {agelocmeLabel}),
        ...(isExclusive && {isExclusive}),
        ...(gpid && {gpid})
    }
};

const _mapLineItemFromSalesOrderDetailLineItemDetail = (lineItemDetail) => {
    if (lineItemDetail == null) {
        return null;
    }

    let lineItem = {};
    if (lineItemDetail.LineItem != null) {
        const agelocmeCode = getValueFromSalesOrderCustom(lineItemDetail.LineItem.Custom, 'ID', 'CUST_MAT'),
            agelocmeName = getValueFromSalesOrderCustom(lineItemDetail.LineItem.Custom, 'NAME', 'CUST_MAT'),
            agelocmeLabel = getValueFromSalesOrderCustom(lineItemDetail.LineItem.Custom, 'LABEL', 'CUST_MAT'),
            isExclusive = getValueFromSalesOrderCustom(lineItemDetail.LineItem.Custom, 'IS_EXCLUSIVE', 'QUALIFICATION'),
            gpid = getValueFromSalesOrderCustom(lineItemDetail.LineItem.Custom, 'GPID', 'GPID');

        lineItem = {
            lineId: lineItemDetail.LineItem.LineID,
            parentId: lineItemDetail.LineItem.ParentID,
            sku: lineItemDetail.LineItem.SKU,
            quantity: lineItemDetail.LineItem.RequestedQuantity,
            payWithPoints: lineItemDetail.LineItem.PayWithPoints,
            oneTime: lineItemDetail.LineItem.OneTime,
            pricing: lineItemDetail.LineItem.Pricing,
            ...(agelocmeCode && {agelocmeCode}),
            ...(agelocmeName && {agelocmeName}),
            ...(agelocmeLabel && {agelocmeLabel}),
            ...(isExclusive && {isExclusive}),
            ...(gpid && {gpid})
        };
    }
    const lineItemDetailValues = {
        title: lineItemDetail.Name,
        points: lineItemDetail.Points,
        pointsUsed: lineItemDetail.PointsUsed,
        psv: lineItemDetail.PSV,
        cv: lineItemDetail.CV,
        sharingBonus: lineItemDetail.SharingBonus,
        freightPromoSavings: lineItemDetail.FreightPromoSavings,
        productPromoSavings: lineItemDetail.ProductPromoSavings,
        price: lineItemDetail.Price,
        taxBase: lineItemDetail.TaxBase,
        replacedSku: lineItemDetail.ReplacedSKU,
        availableQuantity: lineItemDetail.AvailableQuantity,
        totalAvailableQuantity: lineItemDetail.TotalAvailableQuantity,
        tax: lineItemDetail.Tax,
        freight: lineItemDetail.Freight,
        backOrderAvailableDate: lineItemDetail.BackorderAvailableDate,
        backOrdered: lineItemDetail.BackOrdered,
        invalidSku: lineItemDetail.InvalidSku,
        grandFatheredPricing: lineItemDetail.GrandFatheredPricing,
        addedBySystem: lineItemDetail.AddedBySystem,
        plant: lineItemDetail.Plant,
        storageLocation: lineItemDetail.StorageLocation,
        rounding: lineItemDetail.Rounding,
        taxInclusive: lineItemDetail.TaxInclusive
    };
    return {
        ...lineItem,
        ...lineItemDetailValues
    }
};

const _mapSalesOrderDetailShippingParties = (shippingParties) => {
    if (shippingParties == null) {
        return null;
    }

    return shippingParties.map(sp => _mapSalesOrderDetailShippingParty(sp)) || null;
};

const _mapSalesOrderDetailShippingParty = (shippingParty) => {
    if (shippingParty == null) {
        return [];
    }

    const pickupId = getValueFromSalesOrderCustom(shippingParty.Custom, 'TYPE', 'PICKUPID'),
        pickupLocker = getValueFromSalesOrderCustom(shippingParty.Custom, 'TYPE', 'PICKUPSR'),
        addressType = getValueFromSalesOrderCustom(shippingParty.Custom, 'TYPE', 'ADDRESS');

    return {
        shippingMemberId: shippingParty.ShippingMemberID,
        type: shippingParty.Type,
        shippingAddress: _mapSalesOrderShippingPartyAddress(shippingParty.ShippingAddress),
        ...(pickupId && {pickupId}),
        ...(pickupLocker && {pickupLocker}),
        ...(addressType && {addressType})
    };
};

const _mapSalesOrderShippingPartyAddress = (shippingAddress) => {
    if (shippingAddress == null) {
        return null;
    }

    return {
        id: shippingAddress.ID,
        name: shippingAddress.Name,
        careOfName: shippingAddress.CareOfName,
        address: _mapSalesOrderAddress(shippingAddress.Address),
        homePhone: _getContactFromSalesOrder(shippingAddress.Contact, 'HOMEPHONE'),
        phoneCountry: _getContactCountryFromSalesOrder(shippingAddress.Contact, 'HOMEPHONE'),
        mobilePhone: _getContactFromSalesOrder(shippingAddress.Contact, 'MOBILEPHONE'),
        mobilePhoneCountry: _getContactCountryFromSalesOrder(shippingAddress.Contact, 'MOBILEPHONE'),
        email: _getContactFromSalesOrder(shippingAddress.Contact, 'EMAIL'),
        instructions: shippingAddress.Instructions
    };
};

const _getContactCountryFromSalesOrder = (contact, type) => {
    if (contact && contact.length && type && type.length) {
        const cntct = contact.find(c => c.Value.Type === type);
        return cntct && cntct.Country || undefined;
    }
    return undefined;
}

const _getContactFromSalesOrder = (contact, type) => {
    if (contact && contact.length && type && type.length) {
        const cntct = contact.find(c => c.Value.Type === type);
        return cntct && cntct.Value && cntct.Value.value || undefined;
    }
    return undefined;
}

const _mapSalesOrderAddress = (address) => {
    if (address == null) {
        return null;
    }

    return {
        street1: address.Street1,
        street2: address.Street2,
        city: address.City,
        district: address.District,
        districtDesc: address.DistrictDesc,
        region: address.Region,
        regionDesc: address.RegionDesc,
        postalCode: address.PostalCode,
        countryCode: address.CountryCode,
        countryCodeDesc: address.CountryCodeDesc,
        internationalAddresses: address.InternationalAddresses
    }
};

const _mapShippingMethodFromSalesOrderDetail = (shippingMethod) => {
    if (shippingMethod == null) {
        return null;
    }

    return {
        ...(shippingMethod.Code != null && {code: shippingMethod.Code} || {}),
        ...(shippingMethod.Default != null && {default: shippingMethod.Default} || {}),
        ...(shippingMethod.Description != null && {description: shippingMethod.Description} || {}),
        ...(shippingMethod.Cost != null && {cost: shippingMethod.Cost} || {}),
        ...(shippingMethod.PickUpLocation != null && {pickUpLocation: shippingMethod.PickUpLocation} || {}),
        ...(typeof(shippingMethod.Currency) != "undefined" && {currency: shippingMethod.Currency} || {})
    };
};

const _mapToSalesOrderPayment = (payment) => {
    if (payment == null) {
        return null;
    }

    return {
        type: payment.type,
        Currency: payment.currencyCode,
        action: payment.action,
        ...(payment.creditCard != null && { CreditCard: _mapToSalesOrderCreditCard(payment) } || {}),
        ...(payment.bank != null && { Bank: _mapBankToSalesOrder(payment) } || {}),
        BillingAddress: mapBillingAddressToSalesOrder(payment.billingAddress),
        ...(payment.storedCredentialsAgreementDate && {storedCredentialsAgreementDate: payment.storedCredentialsAgreementDate}),
        ...(payment.storedCredentialsNetworkTransactionId && {storedCredentialsNetworkTransactionId: payment.storedCredentialsNetworkTransactionId}),
        ...(payment.storedCredentialsNetworkDateStr && {storedCredentialsNetworkDateStr: payment.storedCredentialsNetworkDateStr}),
        // custom fields
        Custom: [
            ...(typeof payment.paymentTypeId != 'undefined' && [{Key: 'PAYMENT_TYPE', Value: payment.paymentTypeId.toString()}] || []),  // paymentTypeId can be 0
            ...(payment.bePaymentTypeId && [{Key: 'PROFILE_ID', Value: payment.bePaymentTypeId}] || []),
            ...(payment.creditCard && payment.creditCard.accertifyTransactionId && [{Key: 'ACCERTIFY_TRANSACTION_ID', Type: 'ACCERTIFY', Value: payment.creditCard.accertifyTransactionId}] || []),
            ...(payment.type === 'WIRE' && [{Key: 'WIRE', Type: 'CARD_TYPE', Value: payment.ofsId}] || []),
            ...(payment.bank && payment.bank.sepaElectronic && [{Key: 'FLAG_ELECTRONIC', Type: 'SEPA', Value: 'X'}] || []),
            ...(payment.billingAddress && payment.billingAddress.email && [{Key: 'ZZBILLING_EMAIL', Type:'CCBIL', Value: payment.billingAddress.email}] || []),
            ...(payment.billingAddress && payment.billingAddress.phone && [{Key: 'ZZBILLING_PHONE', Type:'CCBIL', Value: payment.billingAddress.phone}] || []),
            ...(payment.billingAddress && payment.billingAddress.address2 && [{Key: 'ZZBILLING_ADDR2', Type: 'CCBIL', Value: payment.billingAddress.address2}] || [])
        ]
    };
};

const _mapPaymentFromSalesOrder = (payment) => {
    if (payment == null) {
        return null;
    }

    const paymentTypeId = parseInt(getValueFromSalesOrderCustom(payment.Custom, 'PAYMENT_TYPE')),
        bePaymentTypeId = getValueFromSalesOrderCustom(payment.Custom, 'PROFILE_ID');

    const ofsId = (payment.CreditCard && payment.CreditCard.Type) ||
        (payment.Bank && payment.Bank.BankKey) ||
        getValueFromSalesOrderCustom(payment.Custom, 'WIRE');

    const paymentNumber = (payment.CreditCard && payment.CreditCard.CardNumber) ||
        (payment.Bank && payment.Bank.IBAN);

    const paymentName = (payment.CreditCard && payment.CreditCard.Name) ||
        (payment.Bank && payment.Bank.AccountHolderName);

    return {
        type: payment.type,
        ofsId,
        currencyCode: payment.Currency,
        action: payment.action,
        paymentNumber,
        paymentName,
        installments: payment.CreditCard && payment.CreditCard.Installments,
        creditCard: _mapCreditCardFromSalesOrder(payment),
        bank: _mapBankFromSalesOrder(payment),
        billingAddress: mapBillingAddressFromSalesOrder(payment),
        ...(payment.storedCredentialsAgreementDate && {storedCredentialsAgreementDate: payment.storedCredentialsAgreementDate}),
        ...(payment.storedCredentialsNetworkTransactionId && {storedCredentialsNetworkTransactionId: payment.storedCredentialsNetworkTransactionId}),
        ...(payment.storedCredentialsNetworkDateStr && {storedCredentialsNetworkDateStr: payment.storedCredentialsNetworkDateStr}),
        // custom fields
        ...(typeof paymentTypeId !== 'undefined' && {paymentTypeId: paymentTypeId}), // paymentTypeId can be 0
        ...(bePaymentTypeId && {bePaymentTypeId})
    };
};

const mapBillingAddressToSalesOrder = (billingAddress) => {
    if (billingAddress == null || billingAddress.address == null) {
        return null;
    }

    // This is at the payment header leave in SAP. Need to figure out exact mappings.
    return {
        address: billingAddress.address,
        ...(billingAddress.district != null &&
            { district: billingAddress.district } ||
            (typeof(billingAddress.district) != "undefined" ? { district: null } : {})),
        city: billingAddress.city,
        state: billingAddress.region,
        ...(billingAddress.country != null &&
            { country: billingAddress.country } ||
            (typeof(billingAddress.country) != "undefined" ? { country: null } : {}))
    };
}

const mapBillingAddressFromSalesOrder = (payment) => {
    const billingAddress = payment.BillingAddress;
    if (billingAddress == null) {
        return null;
    }

    // Map custom
    const phone = getValueFromSalesOrderCustom(payment.Custom, 'ZZBILLING_PHONE', 'CCBIL'),
        email = getValueFromSalesOrderCustom(payment.Custom, 'ZZBILLING_EMAIL', 'CCBIL'),
        address2 = getValueFromSalesOrderCustom(payment.Custom, 'ZZBILLING_ADDR2', 'CCBIL');

    // This is at the payment header leave in SAP. Need to figure out exact mappings.
    return {
        address: billingAddress.address,
        ...(address2 && {address2}),
        ...(billingAddress.district != null &&
            { district: billingAddress.district } ||
            (typeof(billingAddress.district) != "undefined" ? { district: null } : {})),
        city: billingAddress.city,
        region: billingAddress.state,
        ...(billingAddress.country != null &&
            { country: billingAddress.country } ||
            (typeof(billingAddress.country) != "undefined" ? { country: null } : {})),
        ...(payment.CreditCard && payment.CreditCard.PostalCode && {postalCode: payment.CreditCard.PostalCode}),
        ...(phone && {phone}),
        ...(email && {email})
    };
}

const _mapBankToSalesOrder = (payment) => {
    if (payment.bank == null) {
        return null;
    }

    const bank = payment.bank;

    return {
        IBAN: payment.paymentNumber,
        AccountHolderName: payment.paymentName,
        ...(bank.country != null &&
            { BankCountry: bank.country } ||
            ((bank.country != null && typeof(bank.country) != "undefined") ?
                { BankCountry: null } : {})),
        ...(bank.account != null &&
            {BankAccount: bank.account } ||
            ((bank.account != null && typeof(bank.account) != "undefined") ?
                { BankAccount: null} : {})),
        ...(bank.key != null &&
            { BankKey: bank.key } ||
            ((bank.key != null && typeof(bank.key) != "undefined") ?
                { BankKey: null } : {}))
    };
}

const _mapBankFromSalesOrder = (payment) => {
    if (!payment || !payment.Bank) {
        return null;
    }

    const {Bank: bank} = payment,
        sepaElectronic = getValueFromSalesOrderCustom(payment.Custom, 'FLAG_ELECTRONIC', 'SEPA');

    return {
        ...(bank.BankCountry != null &&
            { country: bank.BankCountry } ||
            ((bank.BankCountry != null && typeof(bank.BankCountry) != "undefined") ?
                { country: null } : {})),
        ...(bank.BankAccount != null &&
            { account: bank.BankAccount } ||
            ((bank.BankAccount != null && typeof(bank.BankAccount) != "undefined") ?
                { account: null } : {})),
        ...(bank.BankKey != null &&
            { key: bank.BankKey } ||
            ((bank.BankKey != null && typeof(bank.BankKey) != "undefined") ?
                { key: null } : {})),
        // Custom field mappings
        ...(sepaElectronic === 'X' && {sepaElectronic: true})
    };
};

const _mapCreditCardFromSalesOrder = (payment) => {
    if (!payment || !payment.CreditCard) {
        return null;
    }

    const {CreditCard: creditCard} = payment,
        accertifyTransactionId = getValueFromSalesOrderCustom(payment.Custom, 'ACCERTIFY_TRANSACTION_ID', 'ACCERTIFY');

    return {
        type: creditCard.Type,
        expirationMonth: creditCard.ExpirationMonth,
        expirationYear: creditCard.ExpirationYear,
        verificationCode: creditCard.VerificationCode,
        encryptedCardNumber: creditCard.EncryptedCardNumber,
        trackOne: creditCard.TrackOne,
        // Custom field mappings
        ...(accertifyTransactionId && {accertifyTransactionId})
    };
};

const _mapMessageToSalesOrder = (message) => {
    return {
        BusinessData: message.businessData,
        Code: message.code,
        Message: message.message,
        MsgParameter1: message.msgParameter1,
        MsgParameter2: message.msgParameter2,
        MsgParameter3: message.msgParameter3,
        MsgParameter4: message.msgParameter4,
        Type: message.type
    };
};

const _mapMessageFromSalesOrder = (message) => {
    if (message == null) {
        return null;
    }

    return {
        type: message.Type,
        code: message.Code,
        message: message.Message,
        businessData: message.BusinessData,
        msgParameter1: message.MsgParameter1,
        msgParameter2: message.MsgParameter2,
        msgParameter3: message.MsgParameter3,
        msgParameter4: message.MsgParameter4
    };
};

module.exports = {
    getValueFromSalesOrderCustom,
    mapLineItemsToSalesOrderRequestItem,
    mapLineItemsToSalesOrderDetailLineItems,
    mapShippingToSalesOrderDetailShipping,
    mapToOrderTotals,
    mapToSalesOrderPayments,
    mapLineItemFromSalesOrderItems,
    mapLineItemsFromSalesOrderDetailLineItemDetails,
    mapShippingFromSalesOrderDetailShipping,
    mapOrderTotalsFromSalesOrderDetail,
    mapAdrFromSalesOrderDetail,
    mapPaymentsFromSalesOrder,
    mapMessagesToSalesOrder,
    mapMessagesFromSalesOrder
};
